import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaStar } from 'react-icons/fa';
import BouncingDots from '../Common/BouncingDots';

const testimonials = [
  {
    name: 'Sarah L.',
    role: 'Product Manager',
    company: 'SaaSFlow',
    quote: 'Cyborg Sapient restructured our entire acquisition funnel in 6 weeks. We went from 1.2% to 4.8% landing page CVR. The depth of their analytics setup is unlike any agency we\'d worked with before.',
    metric: '+34%',
    metricLabel: 'Conversion Rate',
    avatar: 'SL',
  },
  {
    name: 'David M.',
    role: 'Founder',
    company: 'StartupX',
    quote: 'They shipped our SaaS MVP in 11 weeks, including a custom admin dashboard and real-time analytics pipeline. Code quality was production-grade from day one — no rewrites needed post-launch.',
    metric: '11 wks',
    metricLabel: 'MVP to Launch',
    avatar: 'DM',
  },
  {
    name: 'Aisha R.',
    role: 'CMO',
    company: 'MarketDash',
    quote: 'What sets them apart is the integration between their marketing and engineering teams. Our campaigns now route leads into a custom CRM they built — response time dropped from 4 hours to 8 minutes.',
    metric: '2.4×',
    metricLabel: 'Engagement Yield',
    avatar: 'AR',
  },
];

const PROOF_ITEMS = [
  { num: '500+', label: 'Projects' },
  { num: '340%', label: 'Avg. ROI' },
  { num: '98%',  label: 'Retention' },
  { num: '4.9',  label: 'Client Rating' },
];

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } };

const TestimonialsSection = () => (
  <section className="w-full bg-white border-t border-[#F1F3F1] relative overflow-hidden">
    <BouncingDots />

    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-40 relative z-10">

      {/* ── Heading ── */}
      <div className="mb-16 sm:mb-24 lg:flex lg:items-end lg:justify-between lg:gap-12">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-6">
            ✦&nbsp;&nbsp;Client Results&nbsp;&nbsp;✦
          </span>
          <h2 className="leading-[1.05] tracking-tight">
            <span className="block text-3xl sm:text-4xl md:text-5xl font-normal text-black/25">
              Numbers tell strategy.
            </span>
            <span className="block text-4xl sm:text-5xl md:text-[64px] font-bold text-black">
              <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">Clients tell the truth.</span>
            </span>
          </h2>
        </motion.div>

        {/* Social proof bar */}
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true }}
          variants={fadeUp} transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 lg:mt-0 flex flex-wrap sm:flex-nowrap gap-0 border border-[#EAECE9] rounded-2xl overflow-hidden divide-x divide-[#EAECE9] bg-[#FAFBF9] flex-shrink-0"
        >
          {PROOF_ITEMS.map((p, i) => (
            <div key={i} className="px-6 py-4 text-center min-w-[90px]">
              <div className="text-xl sm:text-2xl font-bold text-black tabular-nums">{p.num}</div>
              <div className="text-[10px] text-[#5A635A] uppercase tracking-wider font-semibold mt-0.5">{p.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* ── Star row ── */}
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true }}
        variants={fadeUp} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="flex items-center gap-1.5 mb-10"
      >
        {[...Array(5)].map((_, i) => (
          <FaStar key={i} size={14} className="text-[#f97316]" />
        ))}
        <span className="text-sm font-semibold text-black ml-2">4.9</span>
        <span className="text-sm text-[#5A635A] font-normal">&nbsp;/ 5 across 120+ engagements</span>
      </motion.div>

      {/* ── Cards ── */}
      <div className="grid md:grid-cols-3 gap-5">
        {testimonials.map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.75, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#FAFBF9] border border-[#EAECE9] rounded-[24px] p-8 flex flex-col justify-between group hover:border-[#1C2E1E] transition-colors duration-300"
          >
            {/* Quote */}
            <div>
              <div className="text-[3.5rem] leading-none text-black/[0.06] font-serif select-none -ml-1 mb-4">"</div>
              <p className="text-[#1C2E1E] text-[15px] leading-relaxed font-normal mb-8">
                {t.quote}
              </p>
            </div>

            {/* Footer */}
            <div className="border-t border-[#EAECE9] pt-6 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#1C2E1E] flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-white">{t.avatar}</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-black leading-tight">{t.name}</div>
                  <div className="text-[11px] text-[#5A635A] mt-0.5">{t.role}, {t.company}</div>
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-lg font-bold text-black tabular-nums">{t.metric}</div>
                <div className="text-[10px] text-[#5A635A] uppercase tracking-wider font-semibold">{t.metricLabel}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── CTA strip ── */}
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true }}
        variants={fadeUp} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="mt-14 sm:mt-16 bg-[#FAFBF9] border border-[#EAECE9] rounded-[24px] p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
      >
        <div>
          <p className="text-xl sm:text-2xl font-semibold text-black tracking-tight mb-1">
            Ready to become our <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2 font-bold">next success story?</span>
          </p>
          <p className="text-sm text-[#5A635A] font-normal">We respond to every inquiry within 24 hours — no sales bot, no automated pitch.</p>
        </div>
        <div className="flex-shrink-0">
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
            <Link to="/contact"
              className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#1C2E1E] text-white rounded-full text-sm font-semibold hover:bg-black transition-colors duration-200">
              Work With Us
              <FaArrowRight size={11} />
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default TestimonialsSection;
